import { configureProjectSessions, projectWrite } from './project-edit-session';
import axios from 'axios'
import { Message, Loading } from 'element-ui';
import router from '@/permission'; // 导入权限路由文件
import { clearAuth, getAccessToken, getRefreshToken, saveAuth } from '@/utils/auth';

// ===================== 全局配置 =====================
const service = axios.create({
	baseURL: process.env.API_ROOT,
	responseType: 'json',
	withCredentials: true,
	timeout: 3 * 60 * 1000, // 3分钟超时
});

// ===================== 加载动画管理 =====================
let loadingInstance = null;
let loadingNum = 0;
let lastUnauthorizedNoticeAt = 0;

const notifyUnauthorized = (message) => {
	const now = Date.now();
	// 多个并发请求可能同时发现登录失效，短时间内只提示一次。
	if (now - lastUnauthorizedNoticeAt > 2000) {
		Message.error(message);
		lastUnauthorizedNoticeAt = now;
	}
};

const startLoading = () => {
	loadingNum++;
	if (loadingNum === 1) {
		const target = document.querySelector('.content,.content1') || document.body;
		loadingInstance = Loading.service({
			lock: true,
			text: '拼命加载中...',
			background: 'rgba(255,255,255,0.5)',
			target,
		});
	}
};

const endLoading = () => {
	loadingNum = Math.max(0, loadingNum - 1);
	if (loadingNum === 0 && loadingInstance) {
		loadingInstance.close();
		loadingInstance = null;
	}
};

// ===================== 工具函数 =====================
/**
 * 统一转换分页参数：page/limit → current/size
 */
const transformPageParams = (params) => {
	if (!params || typeof params !== 'object') return params;
	const newParams = { ...params };
	if (newParams.limit !== undefined) {
		newParams.size = newParams.limit;
		delete newParams.limit;
	}
	if (newParams.page !== undefined) {
		newParams.current = newParams.page;
		delete newParams.page;
	}
	return newParams;
};

/**
 * 错误消息映射表（后端msg → 前端友好提示）
 */
const ERROR_MSG_MAP = {
	PROJECT_EDIT_SESSION_REQUIRED: '缺少有效编辑会话，已停止提交',
	PROJECT_LOCKED: '项目正由其他编辑会话占用，请稍后重试',
	PROJECT_LOCK_EXPIRED: '编辑会话已过期，本地修改已保留，请重新获取编辑权并加载最新数据',
	PROJECT_LOCK_NOT_OWNED: '当前页面已失去编辑权，已停止提交',
	PROJECT_LOCK_SERVICE_UNAVAILABLE: '编辑会话服务暂不可用，已暂停提交，请稍后确认编辑权',
	PROJECT_STATUS_INVALID_FOR_LOCK: '当前项目状态不允许获取编辑权',
	// 登录相关401
	USER_NOT_EXISTS: '用户不存在',
	INVALID_CREDENTIALS: '密码不正确',
	USER_DISABLED: '用户已被禁用，请联系管理员',
	TOO_MANY_FAILED_ATTEMPTS_LOCKED: '连续登录错误5次，账号已锁定3小时（错误计数器24小时重置）',
	USER_LOCKED: '账号已被锁定，请稍后重试',
	AUTH_FAILED: '登录异常，请稍后重试',

	// Token相关401
	INVALID_TOKEN: 'Token验证失败（Access/Refresh Token不匹配）',
	TOKEN_EXPIRED: '登录状态已过期，正在自动刷新...',
	PLEASE_LOGIN_AGAIN: '登录状态已失效，请重新登录',

	// 500业务错误
	'INTERNAL_ERROR: Access Denied': '暂无接口访问权限，请联系管理员',
	REFRESH_TOKEN_REQUIRED: '刷新Token为空，请重新登录',
	NEW_CONFIRM_PWD_DIFF: '两次输入的新密码不一致',
	INCORRECT_OLD_PASSWORD: '旧密码不正确',
	INVALID_CREDENTIALS: '密码不正确',
	START_TIME_AFTER_END_TIME: '时间范围不合法（开始时间不能晚于结束时间）',
	// CAPTCHA_KEY_REQUIRED: '验证码不正确',
	CAPTCHA_EXPIRED: '验证码已过期',
	CAPTCHA_INVALID: '验证码不正确',
	ONLY_PROJECT_OWNER_CAN_DELETE: '只有项目创建者才能删除',
	ONLY_PROJECT_OWNER_CAN_EXECUTE: '只有项目创建者才能操作',
	STATUS_3_CAN_NOT_EDIT: '项目正在测算中，无法修改项目基础信息，请先解锁后再操作',
	STATUS_3_CAN_NOT_DELETE: '项目正在测算中，无法删除，请先解锁后再操作',
	SHEET_NAME_REQUIRED: 'Sheet名称不能为空',
	SHEET_NAME_TOO_LONG: 'Sheet名称不能超过255个字符',
	SHEET_NAME_ALREADY_EXISTS: '当前项目已存在同名Sheet',
	SHEET_CREATE_FAILED: '新建Sheet失败，请稍后重试',
	SHEET_NOT_FOUND_OR_PROJECT_MISMATCH: 'Sheet不存在或不属于当前项目，请刷新后重试',
	PROJECT_ITEM_AMOUNT_MUST_BE_POSITIVE: '工程数量必须大于0，不能为0',
	PROJECT_ITEM_AMOUNT_MUST_BE_NON_NEGATIVE: '工程数量不能为负数',
	PROJECT_ITEM_SIGN_INVALID: '冲减选项无效，请重新选择后保存',
	PROJECT_SUB_ITEM_TYPE_REQUIRED: '请选择子项目类型',
	SUB_ITEM_TYPE_INVALID: '子项目类型无效，请选择劳务、材料、专业分包或机械',
	SUB_ITEM_NAME_REQUIRED: '请输入子项目名称',
	PROJECT_SUB_ITEM_PRICE_REQUIRED: '请输入子项目单价',
	SUB_ITEM_PRICE_INVALID: '子项目单价不能小于 0',
	PROJECT_SUB_ITEM_UNIT_ID_REQUIRED: '请选择子项目计量单位',
	SUB_ITEM_QUANTITY_INVALID: '子项目消耗量必须大于 0',
	SUB_ITEM_LOSS_INVALID: '损耗率必须在 0%～100% 之间',
	PROJECT_SUB_ITEM_TAX_RATE_REQUIRED: '请输入子项目税率',
	SUB_ITEM_TAX_RATE_INVALID: '税率必须在 0%～100% 之间',
	TRAIN_ID_REQUIRED: '训练ID不能为空，请刷新列表后重试',
	TRAIN_TASK_NOT_FOUND: '训练任务不存在，请刷新列表确认最新状态',
	ONLY_CREATOR_CAN_DELETE_TRAIN_DATA: '只能由训练任务创建者删除训练结果',
	TRAIN_DATA_ALREADY_REMOVED: '训练数据已移除，无需重复删除',
	UNKNOWN_SELF_LEARN_TRAINING_TASK_TYPE: '训练任务类型异常，请刷新列表或联系管理员确认',

	// 默认提示
	DEFAULT: '操作失败，请稍后重试'
};

const SUB_ITEM_BATCH_UPDATE_ERROR_MSG_MAP = {
	PROJECT_ID_REQUIRED: '缺少项目信息，请刷新页面后重试',
	SUB_ITEM_NAME_REQUIRED: '请输入需要批量修改的子项目名称',
	UPDATE_FIELD_REQUIRED: '请选择需要批量修改的字段',
	UPDATE_FIELD_INVALID: '不支持修改该字段',
	UPDATE_VALUE_REQUIRED: '请输入批量修改值',
	SUB_ITEM_PRICE_INVALID: '单价不能小于 0',
	SUB_ITEM_QUANTITY_INVALID: '消耗量必须大于 0',
	SUB_ITEM_TAX_RATE_INVALID: '税率必须在 0%～100% 之间',
	SUB_ITEM_LOSS_INVALID: '损耗率必须在 0%～100% 之间'
};

/**
 * 获取友好的错误提示
 */
const getFriendlyMsg = (msg, requestUrl = '') => {
	if (/\/sub-item\/batch-update(?:[/?#]|$)/.test(requestUrl) && SUB_ITEM_BATCH_UPDATE_ERROR_MSG_MAP[msg]) {
		return SUB_ITEM_BATCH_UPDATE_ERROR_MSG_MAP[msg];
	}
	const unitMismatchPrefix = 'PROJECT_ITEM_UNIT_MISMATCH:';
	if (typeof msg === 'string' && msg.indexOf(unitMismatchPrefix) === 0) {
		const itemName = msg.slice(unitMismatchPrefix.length).trim();
		return itemName
			? `项目“${itemName}”下存在不同的计量单位，请统一计量单位后再进行同类项汇总`
			: '同名项目下存在不同的计量单位，请统一计量单位后再进行同类项汇总';
	}
	return ERROR_MSG_MAP[msg] || ERROR_MSG_MAP.DEFAULT;
};

/**
 * 刷新Token（仅处理TOKEN_EXPIRED场景）
 */
const refreshToken = async () => {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		throw new Error('REFRESH_TOKEN_REQUIRED');
	}
	try {
		const token = getAccessToken();
		const res = await axios.create({
			baseURL: process.env.API_ROOT,
			withCredentials: true,
			timeout: 5000,
			headers:{
				"Authorization": token,
				'Content-Type': 'application/json',
			}
		})({
			url: '/user/auth/refresh',
			method: 'POST',
			data: { refresh_token: refreshToken }
		});
		const { code, data, msg } = res.data;
		if (code !== 200) {
			throw new Error(msg || 'REFRESH_TOKEN_FAILED');
		}
		saveAuth(data);
		return true;
	} catch (err) {
		// 清空Token并抛出错误
		throw new Error(err.message || 'REFRESH_TOKEN_FAILED');
	}
};

// ===================== 请求拦截器 =====================
service.interceptors.request.use(
	(config) => {
		if (config.loading !== false) startLoading();
		// 添加Token请求头
		const token = getAccessToken();
		config.headers = {
			...config.headers,
			"Authorization": token,
		};
		if (config.url.indexOf('import')==-1) {
			config.headers['Content-Type'] = 'application/json';
		}
		// 分页参数转换
		if (config.method === 'get') {
			config.params = transformPageParams(config.params);
		} else if (config.method === 'post' && !(config.data instanceof FormData)) {
			config.data = transformPageParams(config.data);
		}
		return config;
	},
	(error) => {
		endLoading();
		Message.error('请求初始化失败，请稍后重试');
		return Promise.reject({ type: 'REQUEST_INIT_ERROR', msg: error.message, notified: true });
	}
);

// ===================== 响应拦截器 =====================
service.interceptors.response.use(
	(response) => {
		endLoading();
		const config = response.config;
		var requestData = {};
		// 导出请求直接返回原始数据
		if (config.data && typeof config.data === 'string') {
		    requestData = JSON.parse(config.data);
		}
		// const requestData = config.data ? JSON.parse(config.data) : {};
		if (requestData.model === 'export') {
			return response.data;
		}
		// 业务响应处理
		const { code, data, msg } = response.data;
		if (code === 200) {
			return data;
		} else {
			// 业务错误（非200）
			const friendlyMsg = getFriendlyMsg(msg, config.url);
			// 401错误单独标记类型
			if (code === 401) {
				return Promise.reject({
					type: 'AUTH_ERROR',
					code,
					msg: friendlyMsg,
					originalMsg: msg // 保留原始msg便于业务层判断
				});
			}
			// 500错误
			Message.error(friendlyMsg);
			return Promise.reject({
				type: 'BUSINESS_ERROR',
				code,
				msg: friendlyMsg,
				originalMsg: msg,
				notified: true
			});
		}
	},
	(error) => {
		endLoading();
		// 网络错误/超时
		if (!error.response) {
			Message.error('网络异常，请检查网络连接或稍后重试');
			return Promise.reject({ type: 'NETWORK_ERROR', msg: error.message, notified: true });
		}
		// HTTP状态码错误
		const { status, data } = error.response;
		const msg = data && data.msg || '';
		const friendlyMsg = getFriendlyMsg(msg, error.config && error.config.url);

		// 401 HTTP错误（Token/登录相关）
		if (status === 401) {
			return Promise.reject({
				type: 'AUTH_ERROR',
				code: data && data.code || 401,
				msg: friendlyMsg,
				originalMsg: msg
			});
		}
		// 500 HTTP错误（权限/参数等）
		if (status === 500) {
			Message.error(friendlyMsg);
			return Promise.reject({
				type: 'BUSINESS_ERROR',
				code: 500,
				msg: friendlyMsg,
				originalMsg: msg,
				notified: true
			});
		}
		// 其他HTTP错误
		Message.error(`请求失败（${status}）：${friendlyMsg}`);
		return Promise.reject({
			type: 'HTTP_ERROR',
			status,
			msg: friendlyMsg,
			originalMsg: msg,
			notified: true
		});
	}
);

// ===================== 通用请求封装（核心） =====================
/**
 * 通用请求函数（适配后端返回结构的Token刷新+错误处理）
 * @param {Object} options - axios配置
 * @param {Number} retryCount - 已重试次数（默认0，最多1次）
 */
export const request = async (options, retryCount = 0) => {
	const maxRetry = 1; // 仅重试1次Token刷新
	try {
		return await service(options);
	} catch (error) {
		// 仅处理：AUTH_ERROR + originalMsg=TOKEN_EXPIRED + 未超过重试次数
		if (
			error.type === 'AUTH_ERROR' &&
			error.originalMsg === 'TOKEN_EXPIRED' &&
			retryCount < maxRetry
		) {
			try {
				// Message.info(getFriendlyMsg('TOKEN_EXPIRED')); // 提示正在刷新
				await refreshToken(); // 刷新Token
				return await request(options, retryCount + 1); // 重试原请求
			} catch (refreshError) {
				// 刷新失败：区分是refresh_token为空还是其他失败
				const refreshMsg = getFriendlyMsg(refreshError.message);
				notifyUnauthorized(refreshMsg);
				// 跳登录页
				clearAuth();
				// window.location.href = '/login';
				handleUnauthorized()
				return Promise.reject({ type: 'REFRESH_TOKEN_ERROR', msg: refreshMsg, notified: true });
			}
		}

		// 其他AUTH_ERROR（如PLEASE_LOGIN_AGAIN/USER_NOT_EXISTS等）：不重试，直接处理
		if (error.type === 'AUTH_ERROR') {
			// 仅PLEASE_LOGIN_AGAIN跳登录，或者其他非登录流程中的401错误
			if (error.originalMsg === 'PLEASE_LOGIN_AGAIN' || error.originalMsg === 'INVALID_TOKEN') {
				notifyUnauthorized(error.msg || '登录状态已失效，请重新登录');
				error.notified = true;
				handleUnauthorized()
			}
			// 登录失败类错误（如用户不存在/密码错误）不自动提示，由业务层自定义
			return Promise.reject(error);
		}

		// 非授权错误，直接抛出
		return Promise.reject(error);
	}
};

/**
 * 封装POST请求（挂载到Vue原型）
 * @param {String} url - 请求地址
 * @param {Object} data - 请求参数
 * @param {Boolean} loading - 是否显示加载动画
 */
export const post = (url, data, loading = true) => {
	return projectWrite({ url, method: 'POST', data, loading }, request);
};

/**
 * 封装GET请求（可选扩展）
 * @param {String} url - 请求地址
 * @param {Object} params - 请求参数
 * @param {Boolean} loading - 是否显示加载动画
 */
export const get = (url, params, loading = true) => {
	return request({ url, method: 'GET', params, loading });
};
export const export_blob = (url, params, loading = true) => {
	return request({ url, method: 'GET', params, loading, responseType:'blob',data:{model:'export'}});
};
export const export_blob_post = (url, params, loading = true) => {
	return request({ url, method: 'POST', params, loading, responseType:'blob',data:{model:'export'}});
};

configureProjectSessions(request);

export default service;
function handleUnauthorized(){
	clearAuth();
	if (router.currentRoute.path !== '/login') {
		router.push({path: '/login'});
	}
}



// WEBPACK FOOTER //
// ./src/utils/request.js